import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as AppRootActions from '@app/app.actions';
import * as fromAppRoot from '@app/app.reducers';

import { AutoidentItem, IdNowItem } from '@shared/models/IdNow';
import { downloadExternalFile } from '@shared/utils';

import { ToastNotificationService } from './toast-notification.service';
import { environment } from '../../../../environments/environment';
import { ABTest, CreatedPromotions, NewPromotions, PromotionsType, ReferralRewards, VerificationCode } from '../models/Profile';
import { ThirdPartyServiceItem } from '../models/ThirdPartService';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  accountID: string;

  constructor(
    private http: HttpClient,
    private store: Store,
    private translateService: TranslateService,
    private toastNotificationService: ToastNotificationService
  ) {
    this.store.pipe(select(fromAppRoot.selectCurrentAccount)).subscribe((value) => {
      this.accountID = value.id;
    });
  }

  sendVerificationCode(): Observable<VerificationCode> {
    return this.http.post<VerificationCode>(`${environment.apiUrl}/phone_verification/send_code`, {
      manual_resend: true
    });
  }

  verifyVerificationCode(code: string): Observable<VerificationCode> {
    return this.http.post<VerificationCode>(`${environment.apiUrl}/phone_verification/verify_code`, {
      otp_code_token: code
    });
  }

  getPromotions(): Observable<PromotionsType> {
    return this.http.get<PromotionsType>(
      `${environment.apiUrlV3}/customer_promotions?include=promotion&active_rewards=true&pending_rewards=true&referral_rewards=true&aggregated_money_rewards=true`
    );
  }

  postPromotions(payload: NewPromotions): Observable<CreatedPromotions> {
    return this.http.post<CreatedPromotions>(`${environment.apiUrlV3}/promotion_redemptions?include=promotions`, { ...payload });
  }

  getThirdPartyServices(service = ''): Observable<{ data: ThirdPartyServiceItem[] }> {
    if (service) {
      return this.http.get<{ data: ThirdPartyServiceItem[] }>(`${environment.apiUrlV3}/flipper_gates?filter[feature_key]=${service}`);
    }

    return this.http.get<{ data: ThirdPartyServiceItem[] }>(`${environment.apiUrlV3}/flipper_gates`);
  }

  initIdNow(): Observable<{ included: IdNowItem[]; meta: any }> {
    return this.http.post<{ included: IdNowItem[]; meta: any }>(
      `${environment.apiUrlV3}/accounts/${this.accountID}/idnow_ident?include=idnow_identification`,
      {
        data: {
          type: 'idnow_idents',
          attributes: {}
        }
      }
    );
  }

  initAutoident(): Observable<AutoidentItem> {
    return this.http.post<AutoidentItem>(`${environment.apiUrlV4}/onboarding/autoident/accounts/${this.accountID}/identification`, {});
  }

  setIdNowReminder(reminder: Date): Observable<{ data: { attributes: { remind_at } } }> {
    return this.http.put<{ data: { attributes: { remind_at } } }>(`${environment.apiUrlV3}/accounts/${this.accountID}/idnow_identification`, {
      data: {
        id: 0,
        type: 'identifications',
        attributes: {
          remind_at: reminder
        }
      }
    });
  }

  getIdNowReminder(): Observable<{ data: { attributes: { remind_at: string } } }> {
    return this.http.get<{ data: { attributes: { remind_at: string } } }>(`${environment.apiUrlV3}/accounts/${this.accountID}/idnow_identification`);
  }

  downloadCustomerDocuments(fileName: string, typeID: number, documentId: number): void {
    const options = { responseType: 'blob' as 'json' };

    this.http.get<Blob>(`${environment.apiUrl}/documents/${typeID}/${documentId}`, options).subscribe(
      (file) => {
        downloadExternalFile(file, fileName);
      },
      () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.toastNotificationService.show('critical', this.translateService.instant('personal.flash.error_rev'));
      }
    );
  }

  downloadLibertyDocument(fileName: string, documentID: string): void {
    const options = { responseType: 'blob' as 'json' };
    this.http.get<Blob>(`${environment.apiUrlV3}/liberty_documents/${documentID}`, options).subscribe(
      (file) => {
        downloadExternalFile(file, fileName);
      },
      () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.toastNotificationService.show('critical', this.translateService.instant('personal.flash.error_rev'));
      }
    );
  }

  download3aStaticDocument(documentUrl: string): void {
    window.open(documentUrl, '_blank', 'noopener,noreferrer');
  }

  downloadStaticDocument(fileName: string, documentID: string): void {
    const options = { responseType: 'blob' as 'json' };
    this.http.get<Blob>(`${environment.apiUrlV3}/static_documents/${documentID}`, options).subscribe(
      (file) => {
        downloadExternalFile(file, fileName);
      },
      () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.toastNotificationService.show('critical', this.translateService.instant('personal.flash.error_rev'));
      }
    );
  }

  getReferralRewards(referralCode: string): Observable<{ data: { attributes: ReferralRewards } }> {
    return this.http.get<{ data: { attributes: ReferralRewards } }>(`${environment.apiUrlV3}/referral_rewards/${referralCode}`);
  }

  getABTestList(): Observable<{ data: ABTest[]; meta: [] }> {
    return this.http.get<{ data: ABTest[]; meta: [] }>(`${environment.apiUrlV3}/ab_tests`);
  }

  expireMobilePromo(days: number): void {
    const today = new Date();
    const expiryDate = new Date(today.setDate(new Date().getDate() + days)).toUTCString();
    document.cookie = `appPromoSeen=true; Expires=${expiryDate};`;
    this.store.dispatch(AppRootActions.showMobilePromo({ show: false }));
  }

  bookConsultationVPBank(): Observable<unknown> {
    return this.http.post(`${environment.apiUrlV3}/vp/contact_requests`, {});
  }
}
